import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import { TinyMCE_ApiKey } from '../../../config';
import useScopedTranslation from 'hooks/useScopedTranslation';
import { contractGenI18n } from '../i18n';
import ConfirmModal from 'components/ConfirmModal/ConfirmModal';
import {
  sendContractToSignature,
  getContractById,
  terminateContract,
  cancelContract,
  getContractSignedUrl,
  getContractTemplates,
  getContractTemplateHtml,
} from '../../../services/contractGeneratorService';

const STATUS_MAP = {
  1: 'draft',
  2: 'sent',
  3: 'signedByOne',
  4: 'signed',
  5: 'terminated',
  6: 'canceled',
  Draft: 'draft',
  Sent: 'sent',
  SignedByOne: 'signedByOne',
  Signed: 'signed',
  Terminated: 'terminated',
  Canceled: 'canceled',
};

const StepContract = ({ DesignSystemComponents, formData, onChange, contractId: contractIdProp }) => {
  const { Checkbox, Button, IconButton, Modal, Dropdown, Input, Toast } =
    DesignSystemComponents;
  const { t, language: cgLanguage } = useScopedTranslation(contractGenI18n);

  const contractId = contractIdProp || formData?.contractId || formData?.id;

  const [hasInformedConsentDocument] = useState(true);
  const [contractState, setContractState] = useState('draft');
  const [reviewed, setReviewed] = useState(false);
  const [sending, setSending] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const [modalCancelSigning, setModalCancelSigning] = useState(false);
  const [modalOverride, setModalOverride] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [terminating, setTerminating] = useState(false);

  const [generalReason, setGeneralReason] = useState(null);
  const [specificReason, setSpecificReason] = useState('');
  const [createReusableCopy, setCreateReusableCopy] = useState(false);

  const [htmlManual, setHtmlManual] = useState(formData?.contrato?.htmlManual || '');
  const [selectedTemplate, setSelectedTemplate] = useState(formData?.contrato?.template || 'none');
  const [templateOptions, setTemplateOptions] = useState([
    { label: t('cg_contract_no_template'), value: 'none' },
  ]);
  const [loadingTemplate, setLoadingTemplate] = useState(false);

  const GENERAL_REASONS = useMemo(() => [
    { label: t('cg_contract_reason_payment_breach'), value: 'Incumplimiento de pago' },
    { label: t('cg_contract_reason_client_withdrawal'), value: 'Desistimiento del cliente' },
    { label: t('cg_contract_reason_early_termination'), value: 'Término anticipado por acuerdo' },
    { label: t('cg_contract_reason_full_completion'), value: 'Cumplimiento total del servicio' },
    { label: t('cg_contract_reason_other'), value: 'Otro' },
  ], [cgLanguage]);

  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const templates = await getContractTemplates();
        const opts = [
          { label: t('cg_contract_no_template'), value: 'none' },
          ...(templates || []).map((tpl) => ({ label: tpl.name, value: tpl.name })),
        ];
        setTemplateOptions(opts);
      } catch (err) {
        console.error('Error loading templates:', err);
      }
    };
    loadTemplates();
  }, [cgLanguage]);

  const editorManualRef = useRef(null);

  // Load contract status from backend on mount if we have a contractId
  useEffect(() => {
    if (!contractId) return;
    const loadStatus = async () => {
      try {
        const detail = await getContractById(contractId);
        const mapped = STATUS_MAP[detail?.status] || STATUS_MAP[detail?.Status] || 'draft';
        setContractState(mapped);
      } catch (err) {
        console.error('Error loading contract status:', err);
      }
    };
    loadStatus();
  }, [contractId]);

  const stateConfig = useMemo(() => ({
    draft: {
      label: t('cg_contract_status_draft'),
      canSend: true,
      canCancel: false,
      canOverride: false,
      showRefresh: false,
    },
    sent: {
      label: t('cg_contract_status_sent'),
      canSend: false,
      canCancel: true,
      canOverride: false,
      showRefresh: true,
    },
    signedByOne: {
      label: t('cg_contract_status_signed_one'),
      canSend: false,
      canCancel: false,
      canOverride: false,
      showRefresh: true,
    },
    signed: {
      label: t('cg_contract_status_signed'),
      canSend: false,
      canCancel: false,
      canOverride: true,
      showRefresh: false,
    },
    terminated: {
      label: t('cg_contract_status_terminated'),
      canSend: false,
      canCancel: false,
      canOverride: false,
      showRefresh: false,
    },
    canceled: {
      label: t('cg_contract_status_canceled'),
      canSend: false,
      canCancel: false,
      canOverride: false,
      showRefresh: false,
    },
  }), [cgLanguage]);

  const current = stateConfig[contractState] || stateConfig.draft;

  const showToast = (title, message, variant = 'success') => {
    if (Toast?.show) {
      Toast.show({ title, message, variant });
    }
  };

  const handleSendToSignature = async () => {
    if (!contractId) {
      showToast(t('cg_contract_toast_error'), t('cg_contract_toast_not_created'), 'error');
      return;
    }
    setSending(true);
    try {
      const clientName = formData?.cliente?.name || formData?.cliente?.label || '';
      const clientRut = formData?.cliente?.rut || '';
      const clientEmail = formData?.cliente?.email || '';

      const payload = {
        processName: `Contrato ${contractId}`,
        signers: [
          {
            fullName: clientName,
            email: clientEmail,
            identifier: clientRut,
            order: 1,
          },
        ],
        viewers: [],
        tags: ['Legal365', 'Contrato'],
      };

      await sendContractToSignature(contractId, payload);
      setContractState('sent');
      showToast(t('cg_contract_toast_sent'), t('cg_contract_toast_sent_msg'));
    } catch (err) {
      console.error('Error sending to signature:', err);
      showToast(
        t('cg_contract_toast_error'),
        err?.response?.data?.message || err?.message || t('cg_contract_toast_send_error'),
        'error'
      );
    } finally {
      setSending(false);
    }
  };

  const handleRefreshStatus = async () => {
    if (!contractId) return;
    setRefreshing(true);
    try {
      const detail = await getContractById(contractId);
      const mapped = STATUS_MAP[detail?.status] || STATUS_MAP[detail?.Status] || contractState;
      setContractState(mapped);
      showToast(t('cg_contract_toast_updated'), t('cg_contract_toast_updated_msg', { status: stateConfig[mapped]?.label || mapped }));
    } catch (err) {
      console.error('Error refreshing status:', err);
      showToast(t('cg_contract_toast_error'), t('cg_contract_toast_update_error'), 'error');
    } finally {
      setRefreshing(false);
    }
  };

  const handleCancelSignature = async () => {
    if (!contractId) return;
    setCanceling(true);
    try {
      await cancelContract(contractId, { reason: 'Cancelación de firma por usuario' });
      setContractState('draft');
      setReviewed(false);
      setModalCancelSigning(false);
      showToast(t('cg_contract_toast_canceled'), t('cg_contract_toast_canceled_msg'));
    } catch (err) {
      console.error('Error canceling signature:', err);
      showToast(t('cg_contract_toast_error'), err?.response?.data?.message || t('cg_contract_toast_cancel_error'), 'error');
    } finally {
      setCanceling(false);
    }
  };

  const handleTerminate = async () => {
    if (!contractId) return;
    setTerminating(true);
    try {
      await terminateContract(contractId, {
        reasonCategory: generalReason?.value || '',
        reasonDetail: specificReason || '',
        createReusableCopy,
      });
      setContractState('terminated');
      setModalOverride(false);
      setGeneralReason(null);
      setSpecificReason('');
      showToast(t('cg_contract_toast_terminated'), t('cg_contract_toast_terminated_msg'));
    } catch (err) {
      console.error('Error terminating:', err);
      showToast(t('cg_contract_toast_error'), err?.response?.data?.message || t('cg_contract_toast_terminate_error'), 'error');
    } finally {
      setTerminating(false);
    }
  };

  const handleDownloadContract = async () => {
    if (!contractId) return;
    try {
      const data = await getContractSignedUrl(contractId);
      if (data?.url) {
        window.open(data.url, '_blank');
      } else {
        showToast(t('cg_contract_toast_no_file'), t('cg_contract_toast_no_file_msg'), 'warning');
      }
    } catch (err) {
      console.error('Error downloading contract:', err);
      showToast(t('cg_contract_toast_error'), t('cg_contract_toast_download_error'), 'error');
    }
  };

  const CONTRACT_VARIABLES = useMemo(() => [
    { key: 'clienteNombre', label: t('cg_contract_var_client_name'), resolve: () => formData?.cliente?.name || formData?.cliente?.label || '' },
    { key: 'clienteRut', label: t('cg_contract_var_client_rut'), resolve: () => formData?.cliente?.rut || '' },
    { key: 'clienteEmail', label: t('cg_contract_var_client_email'), resolve: () => formData?.cliente?.email || '' },
    { key: 'clienteTelefono', label: t('cg_contract_var_client_phone'), resolve: () => formData?.cliente?.phone || '' },
    { key: 'lineaDeNegocio', label: t('cg_contract_var_business_line'), resolve: () => formData?.lineaDeNegocio?.label || '' },
    { key: 'servicio', label: t('cg_contract_var_service'), resolve: () => formData?.servicio?.label || '' },
    { key: 'costoTotal', label: t('cg_contract_var_total_cost'), resolve: () => {
      const total = Number(formData?.costos?.costoTotal || 0);
      return total > 0 ? new Intl.NumberFormat(undefined, { style: 'currency', currency: 'CLP' }).format(total) : '';
    }},
    { key: 'numeroCuotas', label: t('cg_contract_var_number_fees'), resolve: () => String(formData?.costos?.numeroCuotas || 1) },
    { key: 'valorCuota', label: t('cg_contract_var_fee_value'), resolve: () => {
      const val = Number(formData?.costos?.valorPorCuota || formData?.costos?.costoTotal || 0);
      return val > 0 ? new Intl.NumberFormat(undefined, { style: 'currency', currency: 'CLP' }).format(val) : '';
    }},
    { key: 'fechaPago', label: t('cg_contract_var_payment_date'), resolve: () => formData?.costos?.fechaPago || '' },
    { key: 'fechaActual', label: t('cg_contract_var_current_date'), resolve: () => new Date().toLocaleDateString() },
    { key: 'codigoContrato', label: t('cg_contract_var_contract_code'), resolve: () => contractId ? `C-${contractId}` : '' },
  ], [cgLanguage, formData, contractId]);

  const setupTinyMCEVariables = (editor) => {
    editor.ui.registry.addMenuButton('variablesMenu', {
      text: t('cg_contract_variables_menu'),
      tooltip: t('cg_contract_variables_tooltip'),
      fetch: (callback) => {
        const items = CONTRACT_VARIABLES.map((v) => ({
          type: 'menuitem',
          text: v.label,
          onAction: () => {
            editor.insertContent(`<strong>{{${v.key}}}</strong>`);
          },
        }));
        callback(items);
      },
    });

    editor.ui.registry.addMenuButton('replaceVariables', {
      text: t('cg_contract_replace_menu'),
      tooltip: t('cg_contract_replace_tooltip'),
      fetch: (callback) => {
        callback([{
          type: 'menuitem',
          text: t('cg_contract_replace_all'),
          onAction: () => {
            let content = editor.getContent();
            CONTRACT_VARIABLES.forEach((v) => {
              const regex = new RegExp(`\\{\\{${v.key}\\}\\}`, 'g');
              content = content.replace(regex, v.resolve());
            });
            editor.setContent(content);
          },
        }]);
      },
    });
  };

  const handleManualEditorChange = (content) => {
    setHtmlManual(content);
    onChange({
      contrato: {
        ...(formData?.contrato || {}),
        modo: 'manual',
        htmlManual: content,
      },
    });
  };

  const isDraft = contractState === 'draft';

  return (
    <div className='step-content mb-4'>
      <div className='d-flex justify-content-between'>
        <p className='body fw-semibold mb-2'>{t('cg_contract_title')}</p>
        <div className='d-flex align-items-center'>
          <p className='body me-2'>
            {t('cg_contract_status')}<span className='fw-semibold'>{current.label}</span>
          </p>
          {contractState === 'signed' && (
            <div className='d-flex'>
              <IconButton
                ariaLabel={t('cg_contract_download_aria')}
                icon='download'
                onClick={handleDownloadContract}
                size='lg'
                className='me-2'
              />
            </div>
          )}
          {current.showRefresh && (
            <IconButton
              ariaLabel={t('cg_contract_refresh_aria')}
              icon='arrowCounterclockwise'
              onClick={handleRefreshStatus}
              size='lg'
              disabled={refreshing}
            />
          )}
        </div>
      </div>

      <div className='row'>
        <div className='col-6'>
          <div className='space-like-input'>
            <Dropdown
              label={t('cg_contract_template_label')}
              onChange={async (value) => {
                const tpl = value?.value || value || 'none';
                setSelectedTemplate(tpl);
                if (tpl !== 'none') {
                  setLoadingTemplate(true);
                  try {
                    const html = await getContractTemplateHtml(tpl);
                    setHtmlManual(html);
                    onChange({
                      contrato: { ...(formData?.contrato || {}), modo: 'manual', template: tpl, htmlManual: html },
                    });
                  } catch (err) {
                    console.error('Error loading template:', err);
                    showToast(t('cg_contract_toast_error'), t('cg_contract_toast_template_error'), 'error');
                  } finally {
                    setLoadingTemplate(false);
                  }
                } else {
                  onChange({
                    contrato: { ...(formData?.contrato || {}), modo: 'manual', template: tpl, htmlManual },
                  });
                }
              }}
              options={templateOptions}
              value={templateOptions.find((o) => o.value === selectedTemplate) || templateOptions[0]}
              disabled={!isDraft || loadingTemplate}
            />
          </div>
        </div>
        <div className='col-6 d-flex align-items-center justify-content-end'>
          {isDraft && (
            <>
              <Checkbox
                id='reviewed-doc'
                checked={reviewed}
                onChange={(e) => setReviewed(e.target.checked)}
                label={t('cg_contract_reviewed')}
                className='me-4'
              />
              <Button
                label={sending ? t('cg_contract_sending') : t('cg_contract_send_to_sign')}
                onClick={handleSendToSignature}
                variant='primary'
                disabled={!reviewed || sending}
              />
            </>
          )}
          {(contractState === 'sent' || contractState === 'signedByOne') && (
            <Button
              label={t('cg_contract_cancel_signature')}
              onClick={() => setModalCancelSigning(true)}
              variant='primary'
            />
          )}
          {contractState === 'signed' && (
            <Button
              label={t('cg_contract_terminate')}
              onClick={() => setModalOverride(true)}
              variant='primary'
            />
          )}
          {contractState === 'terminated' && (
            <Button
              label={t('cg_contract_reuse')}
              onClick={() => {
                setContractState('draft');
                setReviewed(false);
              }}
              variant='secondary'
            />
          )}
        </div>
      </div>

      <div className='row mb-2'>
        <div className='col-12'>
          <div className='warning-message'>
            {hasInformedConsentDocument ? (
              <>
                <p className='caption mb-3'>
                  {t('cg_contract_informed_consent_msg')}
                </p>
                <p className='caption'>
                  {t('cg_contract_informed_consent_msg2')}
                </p>
              </>
            ) : (
              <p className='caption'>
                {t('cg_contract_no_consent_msg')}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className='mb-4'>
        <Editor
          apiKey={TinyMCE_ApiKey}
          onInit={(_, editor) => (editorManualRef.current = editor)}
          value={htmlManual}
          onEditorChange={handleManualEditorChange}
          init={{
            height: 800,
            menubar: true,
            plugins:
              'advlist autolink lists link charmap preview anchor ' +
              'searchreplace visualblocks code fullscreen ' +
              'insertdatetime table code help wordcount',
            toolbar:
              'undo redo | formatselect | bold italic underline | ' +
              'alignleft aligncenter alignright | ' +
              'bullist numlist outdent indent | removeformat | variablesMenu replaceVariables',
            setup: setupTinyMCEVariables,
            branding: false,
            language: 'es',
          }}
          disabled={!isDraft}
        />
      </div>

      {/* Modal: Cancelar firma */}
      <ConfirmModal
        Modal={DesignSystemComponents.Modal}
        show={modalCancelSigning}
        title={t('cg_contract_cancel_modal_title')}
        message={t('cg_contract_cancel_modal_msg')}
        confirmLabel={canceling ? t('cg_contract_canceling') : t('cg_contract_cancel_btn')}
        confirmVariant='danger'
        onConfirm={handleCancelSignature}
        onClose={() => setModalCancelSigning(false)}
        disabledConfirm={canceling}
      />

      {/* Modal: Finiquitar */}
      <Modal
        onClose={() => {
          setModalOverride(false);
          setGeneralReason(null);
          setSpecificReason('');
        }}
        show={modalOverride}
        primaryBtnAction={handleTerminate}
        primaryBtnTxt={terminating ? t('cg_contract_terminating') : t('cg_contract_terminate_btn')}
        disabledPrimaryBtn={!generalReason || terminating}
        secondaryBtnAction={() => setModalOverride(false)}
        secondaryBtnTxt={t('cg_contract_terminate_cancel')}
        title={t('cg_contract_terminate_modal_title')}
        width='600px'
      >
        <div className='row'>
          <div className='col-12'>
            <Dropdown
              label={t('cg_contract_general_reason')}
              menuPlacement='bottom'
              onChange={(value) => setGeneralReason(value)}
              options={GENERAL_REASONS}
              placeholder={t('cg_contract_reason_placeholder')}
              required
              className='space-like-input'
              value={generalReason}
            />
            <div className='mt-3'>
              <Input
                label={t('cg_contract_specific_reason')}
                placeholder={t('cg_contract_specific_reason_placeholder')}
                value={specificReason}
                onChange={(e) => setSpecificReason(e?.target?.value || e)}
              />
            </div>
            <div className='mt-3'>
              <Checkbox
                id='create-reusable-copy'
                checked={createReusableCopy}
                onChange={(e) => setCreateReusableCopy(e.target.checked)}
                label={t('cg_contract_create_reusable_copy')}
              />
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default StepContract;
